/**
 * @license
 * Copyright TWISS.IO
 * All Rights Reserved.
 * Licensed under the MIT License.
 */

import { Component, Inject } from "@angular/core";
import { Subscription } from "rxjs";
import { saveAs } from "file-saver";
import { MatDialogRef } from "@angular/material/dialog";
import { ApiService } from "@app/@core/services";
import { MAT_DIALOG_DATA } from "@angular/material";
import { AdminCaseSubmissionsService } from "@app/@shared/services/admin/case-submissions/case-submissions.service";
import { MessageService } from "@app/@shared/components/message/message.service";

@Component({
  selector: "admin-submission-media-dialog",
  templateUrl: "./submission-media-dialog.component.html",
  styleUrls: ["./submission-media-dialog.component.scss"],
})
export class AdminSubmissionMediaDialogComponent {
  private mediaSub: Subscription;
  private caseSub: Subscription;
  dataLoaded: boolean = false;
  downloading: boolean = false;
  media;
  cases;

  upload_types = [
    { title: "Identity Documents", slug: "client-identity", pos: false },
    // { title: "Selected Client with No Trusted Individual (Written Acknowledgement)", slug: "no-ti-written-acknowledgement", pos: false },
    { title: "Proof of Address", slug: "proof-of-address", pos: false },
    { title: "Personal Financial Records (PFR)", slug: "pfr", pos: true },
    { title: "Submission Checklist", slug: "submission-checklist", pos: false },
    { title: "Other Documents", slug: "other-documents", pos: false },
  ];

  case_upload_types = [
    { title: "Application Forms", slug: "application-form", exists: ["insurance", "cis"], pos: true },
    { title: "Benefit Illustrations", slug: "benefit-illustration", exists: ["insurance"], pos: true },
    { title: "GIRO Forms", slug: "giro-form", exists: ["insurance", "cis"], pos: true },
    { title: "Medical Reports", slug: "medical-report", exists: ["insurance"], pos: false },
    { title: "Supplementary Forms/Questionaires", slug: "supplementary", exists: ["insurance"], pos: false },
    { title: "Other Documents (To Client)", slug: "documents-send", exists: ["insurance", "cis", "gi", "loans", "wills"], pos: true },
    { title: "Other Documents (Internal Archival)", slug: "documents", exists: ["insurance", "cis", "gi", "loans", "wills"], pos: false },
  ];

  constructor(@Inject(MAT_DIALOG_DATA) private injected_data: any, private _notification: MessageService, public assSubmissionService: AdminCaseSubmissionsService, private apiService: ApiService, public dialogRef: MatDialogRef<AdminSubmissionMediaDialogComponent>) {}

  ngOnInit() {
    this.getMedia();
    this.getCases();
  }

  ngOnDestroy() {
    if (this.mediaSub) this.mediaSub.unsubscribe();
    if (this.caseSub) this.caseSub.unsubscribe();
  }

  getMedia() {
    if (this.injected_data) {
      this.dataLoaded = false;
      this.mediaSub = this.assSubmissionService.getMedia(this.injected_data).subscribe((res) => {
        let data = res.data;
        this.media = data;
        this.dataLoaded = true;
      });
    }
  }

  getCases() {
    if (this.injected_data) {
      this.dataLoaded = false;
      this.caseSub = this.assSubmissionService.getCases(this.injected_data).subscribe((res) => {
        let data = res.data;
        this.cases = data;
      });
    }
  }

  downloadMedia(file_id, file_name) {
    this.downloading = true;
    setTimeout(() => {
      this.apiService
        .download("admin/submissions/" + this.injected_data + "/media/" + file_id)
        .toPromise()
        .then((blob) => {
          if (blob) saveAs(blob, file_name);
          else this._notification.create("danger", "Error: File does not exists on the server.", { Position: "top", Style: "bar", Duration: 2000 });
          this.downloading = false;
        });
    }, 1000);
  }

  downloadCompressedMedia(file_id, file_name) {
    this.downloading = true;
    setTimeout(() => {
      this.apiService
        .download("admin/submissions/" + this.injected_data + "/media/" + file_id + "/compressed")
        .toPromise()
        .then((blob) => {
          if (blob) saveAs(blob, file_name);
          else this._notification.create("danger", "Error: File does not exists on the server.", { Position: "top", Style: "bar", Duration: 2000 });
          this.downloading = false;
        });
    }, 1000);
  }

  downloadCaseMedia(file_id, file_name, case_uuid) {
    this.downloading = true;
    setTimeout(() => {
      this.apiService
        .download("admin/submissions/" + this.injected_data + "/cases/" + case_uuid + "/documents/" + file_id)
        .toPromise()
        .then((blob) => {
          if (blob) saveAs(blob, file_name);
          else this._notification.create("danger", "Error: File does not exists on the server.", { Position: "top", Style: "bar", Duration: 2000 });
          this.downloading = false;
        });
    }, 1000);
  }

  downloadCaseCompressedMedia(file_id, file_name, case_uuid) {
    this.downloading = true;
    setTimeout(() => {
      this.apiService
        .download("admin/submissions/" + this.injected_data + "/cases/" + case_uuid + "/documents/" + file_id + "/compressed")
        .toPromise()
        .then((blob) => {
          if (blob) saveAs(blob, file_name);
          else this._notification.create("danger", "Error: File does not exists on the server.", { Position: "top", Style: "bar", Duration: 2000 });
          this.downloading = false;
        });
    }, 1000);
  }

  groupBy(items, key) {
    items.reduce(
      (result, item) => ({
        ...result,
        [item[key]]: [...(result[item[key]] || []), item],
      }),
      {}
    );
  }

  mediaByType(type) {
    if (!this.media) return [];
    return this.media.filter((item) => item.collection_name == type) || [];
  }

  caseMediaByType(data, type) {
    if (!data) return [];
    return data.filter((item) => item.collection_name == type) || [];
  }
}
